/**
 * THE CLOCK ITSELF — anchored to wall time, and pure (BUD-284).
 *
 * Everything here is a function of a TIMESTAMP and a spec. Nothing counts, nothing
 * accumulates, nothing is decremented, and there is no React, no DOM and no `Date.now()`
 * inside it — `now` arrives as an argument. That is what makes the whole mechanism testable
 * without rendering anything, which matters because the component around it reaches the
 * platform seam and cannot be driven from a spec.
 *
 * 🔑 WHY NOT COUNT. A `setInterval` that subtracts one per tick is the obvious clock and it
 * is wrong in the one condition this screen is always in: the phone goes on the floor, the
 * app is backgrounded or the tab is hidden, and the browser throttles timers to roughly once
 * a minute. A counter then runs SLOW — a "20 minute" AMRAP finishes after twenty-six real
 * minutes, silently, and the athlete has no way to know. Recomputing from the wall clock
 * cannot drift, because it never accumulates: every answer is derived from scratch from two
 * timestamps.
 *
 * 🔑 WHY A `while` LOOP AND NOT A COMPARISON. The same throttling means a single tick can
 * arrive having crossed FOUR minute boundaries. A phase step that advances by one per tick
 * would be four minutes behind and would stay four minutes behind for the rest of the piece.
 * {@link positionAt} walks forward until it reaches the phase that contains the elapsed
 * time, however many that takes, and {@link phasesEntered} reports every boundary that was
 * crossed so the caller can act on each of them once.
 *
 * 🔑 WHY THE ANCHOR IS THREE NUMBERS AND NEVER A COUNTDOWN VALUE. What is persisted is
 * `startedAt`, `pausedMs` and `pausedAt` — the SHAPE of the run, not a snapshot of it. A
 * stored "14:32 remaining" is a lie the moment the app is put away, because nothing was
 * awake to keep counting it down; a stored start instant is still true after a relaunch
 * three minutes later, and the three minutes are correctly part of the piece.
 */

import type { PacedBy, ScoredBy } from "@shared/contracts";

/* -----------------------------------------------------------------------------------------
 * THE SPEC — what the piece is made of
 * -------------------------------------------------------------------------------------- */

/**
 * ONE PHASE OF A PIECE.
 *
 * `durationSec` of `null` is the load-bearing case and it is not "missing": it means the
 * phase is OPEN-ENDED — it ends when the athlete says so, not when the clock does. A round
 * of a for-time piece is open-ended; a minute of an EMOM is not. The whole difference
 * between a clock that paces the athlete and a clock the athlete paces is this one field.
 *
 * `id` is a stable handle for the layouts (BUD-285) to key off. This module never reads it.
 */
export interface TimerPhase {
  id: string;
  kind: "work" | "rest";
  durationSec: number | null;
}

/**
 * A PIECE, as the clock sees it.
 *
 * `capSec` is the ceiling: the piece auto-finishes when it is reached, with no confirmation.
 * `null` is genuinely uncapped — an athlete running a for-time piece with no time cap
 * presses Finish, and a fabricated ceiling would stop their clock and file the attempt as
 * capped.
 */
export interface TimerSpec {
  phases: TimerPhase[];
  capSec: number | null;
}

/**
 * DOES THE CLOCK SET THE PACE, OR DOES THE ATHLETE? — derived, never stored twice.
 *
 * A piece is structure-paced when every phase it has knows its own length, so the whole
 * sequence of boundaries is known before the piece starts. That is exactly the condition the
 * audio schedule needs (see {@link pieceTones}), which is why this is one predicate read in
 * both places rather than a flag someone could set to disagree with the phases.
 *
 * A piece with no phases at all is NOT structure-paced: an empty sequence knows no
 * boundaries, so promising boundary tones for it would promise nothing.
 */
export function isStructurePaced(spec: TimerSpec): boolean {
  return spec.phases.length > 0 && spec.phases.every((phase) => phase.durationSec != null);
}

/* -----------------------------------------------------------------------------------------
 * THE ANCHOR — the three numbers that survive everything
 * -------------------------------------------------------------------------------------- */

/**
 * WHERE THE PIECE IS ANCHORED IN WALL TIME.
 *
 * - `startedAt` — the instant the clock started, which is when the countdown ENDED and never
 *   when the athlete tapped. Stamping it at the tap would quietly turn a 20:00 cap into
 *   19:57.
 * - `pausedMs` — everything spent paused and already banked, from pauses that are over.
 * - `pausedAt` — the instant the CURRENT pause began, or null while the clock is running.
 *
 * Three numbers rather than two because a pause has to be representable while it is still
 * happening: folding it into `pausedMs` at the moment it starts would require knowing how
 * long it would last.
 */
export interface TimerAnchor {
  startedAt: number;
  pausedMs: number;
  pausedAt: number | null;
}

/**
 * HOW LONG THE PIECE HAS BEEN RUNNING, in milliseconds.
 *
 * `elapsed = (pausedAt ?? now) - startedAt - pausedMs`. While paused the clock is frozen at
 * the instant the pause began, which is what makes a pause that outlasts the app still be
 * the right length when it is resumed.
 *
 * Floored at zero, so a clock read a millisecond before its own start (a rounding difference
 * between the instant that was stamped and the instant that is read) reports 0 rather than a
 * negative number that would format as `-0:01`.
 */
export function elapsedMs(anchor: TimerAnchor, now: number): number {
  const until = anchor.pausedAt ?? now;
  return Math.max(0, until - anchor.startedAt - anchor.pausedMs);
}

/** The same figure in whole seconds — the unit everything below is in. */
export function elapsedSec(anchor: TimerAnchor, now: number): number {
  return Math.floor(elapsedMs(anchor, now) / 1000);
}

/* -----------------------------------------------------------------------------------------
 * WHERE THE PIECE IS
 * -------------------------------------------------------------------------------------- */

/** Everything a screen needs to render one frame of a running piece. */
export interface TimerPosition {
  /** Index into `spec.phases`, clamped to the last one. */
  phaseIndex: number;
  /** The elapsed second at which the current phase began. */
  phaseStartedSec: number;
  /** Seconds spent in the current phase. */
  intoPhaseSec: number;
  /** Seconds left in the current phase, or null when the phase is open-ended. */
  phaseRemainingSec: number | null;
  /** Seconds since the clock started, CLAMPED at the cap — see below. */
  elapsedSec: number;
  /** Seconds left before the cap, or null when the piece is uncapped. */
  capRemainingSec: number | null;
  /**
   * WHY THE PIECE STOPPED, or null while it is still running.
   *
   * TWO DIFFERENT ENDINGS, AND THEY MUST NOT BE CONFLATED. `cap` is the clock running out on
   * an athlete mid-round — the piece was capped and they did not finish it. `structure` is a
   * piece that simply reached the end of its own phases, which is a COMPLETION. Reporting
   * both as "capped" tells someone who finished a 20-round piece with no time cap that they
   * hit a cap they never had, and BUD-285 turns this into copy and into a score.
   *
   * A boolean here is what made that mistake possible, so there is no boolean: ask
   * `finishedBy != null`.
   */
  finishedBy: "cap" | "structure" | null;
}

/**
 * HOW A PIECE ENDED, INCLUDING THE ONE ENDING THE CLOCK CANNOT SEE.
 *
 * `TimerPosition.finishedBy` above answers only what the CLOCK can work out — the cap arrived,
 * or the phases ran out. The third ending is the athlete swiping to finish, which no arithmetic
 * over an anchor will ever produce. This union is the whole set, and it is declared here rather
 * than inside `use-timer.ts` so the STORE can hold it: BUD-289's debrief reads how the piece
 * ended off the store by session id, and a type living in the hook would make the store import
 * the hook that already imports it.
 */
export const TIMER_FINISH_REASONS = ["cap", "structure", "athlete"] as const;

/**
 * ⚠️ THE LIST ABOVE IS THE TYPE, and it is a list because the stored clock has to be VALIDATED
 * rather than trusted: an ending read back off a plist is a string until something checks it
 * against the set, and a second copy of that set written out by hand at the parse site is the
 * thing that goes stale the day a fourth ending is added.
 */
export type TimerFinishReason = (typeof TIMER_FINISH_REASONS)[number];

/**
 * Where the clock is, walked forward from a known starting point.
 *
 * `fromIndex` / `fromStartedSec` say which phase was last ENTERED and when — which is the
 * only way an open-ended phase can be positioned at all, because nothing about the elapsed
 * time says when the athlete finished a round. A structure-paced piece passes `0, 0` and
 * gets the whole sequence derived from elapsed alone.
 *
 * 🔑 THE LOOP IS THE POINT. It advances while the current phase is TIMED and its end has
 * already passed, so one call crosses however many boundaries the elapsed time demands — one
 * on a healthy tick, four after the phone spent four minutes in a pocket. It stops on an
 * open-ended phase, because only a tap ends one of those, and on the last phase, because
 * there is nowhere further to go.
 *
 * THE CLOCK NEVER TICKS PAST ITS OWN END, whichever end that is. `elapsedSec` is clamped at
 * the cap, so a piece that finished at 20:00 reads 20:00 and not 20:03 — a screen that keeps
 * counting after the cap is confidently wrong about the one number the athlete is going to
 * write down.
 *
 * ⚠️ AND THE SAME CLAMP APPLIES WHEN THE STRUCTURE RUNS OUT (BUD-285). Originally only the cap
 * clamped, because every piece with a fixed length also carried a `capSec`. It no longer
 * does: a format that declares `durationIsFixed` gets PHASES adding up to its length and no
 * ceiling, precisely so that reaching the end reads as finishing rather than as being capped
 * (`timer.converter.ts`). Without this second clamp an 18:00 AMRAP whose tick landed two
 * seconds late logged 18:02 — and a tick that landed after a minute in a pocket would have
 * logged 18:59. The score of an AMRAP is the rounds, but the elapsed time is what a for-time
 * piece is scored ON, so a clock that reports how late the tick was is reporting a defect.
 */
export function positionAt(
  spec: TimerSpec,
  rawElapsedSec: number,
  fromIndex = 0,
  fromStartedSec = 0,
): TimerPosition {
  const capped = spec.capSec != null && rawElapsedSec >= spec.capSec;
  const elapsed = Math.max(0, capped && spec.capSec != null ? spec.capSec : rawElapsedSec);

  let index = Math.min(Math.max(0, fromIndex), Math.max(0, spec.phases.length - 1));
  let startedSec = Math.max(0, fromStartedSec);
  let ranOut = false;

  // The catch-up. See the header: a single tick may have to cross several boundaries.
  for (;;) {
    const phase = spec.phases[index];
    if (phase?.durationSec == null) break;
    const endsSec = startedSec + phase.durationSec;
    if (elapsed < endsSec) break;
    if (index >= spec.phases.length - 1) {
      // The last phase is timed and its end has passed: the piece has run out of structure.
      // Held here rather than wrapping — a sequence read modulo its own length reports
      // "round 1 of 8" forever, which is the one reading worse than a stopped clock because
      // it looks live.
      ranOut = true;
      break;
    }
    index += 1;
    startedSec = endsSec;
  }

  const phase = spec.phases[index];
  const duration = phase?.durationSec ?? null;
  const intoPhaseSec = Math.max(0, elapsed - startedSec);

  /**
   * WHERE THE PIECE ACTUALLY ENDED, which is not the same as when anybody noticed. A structure
   * that ran out ended at its last phase's own end; a late, throttled or backgrounded tick is
   * what discovered it. See the note in the header — this is the second half of "the clock
   * never ticks past its own end".
   */
  const reported = ranOut && duration != null ? startedSec + duration : elapsed;

  return {
    phaseIndex: index,
    phaseStartedSec: startedSec,
    intoPhaseSec: ranOut && duration != null ? duration : intoPhaseSec,
    phaseRemainingSec:
      duration == null ? null : Math.max(0, duration - (ranOut ? duration : intoPhaseSec)),
    elapsedSec: reported,
    capRemainingSec: spec.capSec == null ? null : Math.max(0, spec.capSec - reported),
    // The cap wins when both are true: a piece whose last phase ends exactly at its cap was
    // stopped by the clock, and that is the harsher and therefore the honest reading.
    finishedBy: capped ? "cap" : ranOut ? "structure" : null,
  };
}

/**
 * WHICH PHASES WERE ENTERED SINCE THE LAST LOOK — one entry per boundary actually crossed.
 *
 * The caller fires a haptic, a tone or a layout change for each of these. Returning the LIST
 * rather than a boolean is what makes a throttled catch-up honest: an app that comes back
 * having missed three minute boundaries has genuinely entered three minutes, and the screen
 * that reports "you are in minute 7" without ever having said 5 or 6 is the one an athlete
 * cannot reconcile with the work they just did.
 *
 * Empty when nothing changed, and empty when the index went BACKWARDS — which it should
 * never do, and which a recovery that re-seeds from a stale store could produce. Replaying a
 * boundary the athlete already heard is worse than missing the report of a move that did not
 * happen.
 */
export function phasesEntered(previousIndex: number, position: TimerPosition): number[] {
  const entered: number[] = [];
  for (let index = previousIndex + 1; index <= position.phaseIndex; index++) entered.push(index);
  return entered;
}

/**
 * THE ATHLETE ENDS AN OPEN-ENDED PHASE — the tap that a self-paced piece runs on.
 *
 * Returns where the next phase begins, which is NOW rather than any point derived from the
 * spec: an open-ended phase has no length, so the only thing that can say when the next one
 * started is the moment the tap landed. On the last phase it answers unchanged — there is
 * nowhere to advance to, and the piece ends through Finish.
 */
export function advancePhase(
  spec: TimerSpec,
  position: TimerPosition,
): { index: number; startedSec: number } {
  if (position.phaseIndex >= spec.phases.length - 1) {
    return { index: position.phaseIndex, startedSec: position.phaseStartedSec };
  }
  return { index: position.phaseIndex + 1, startedSec: position.elapsedSec };
}

/* -----------------------------------------------------------------------------------------
 * THE PIECE AS THE TWO LAYOUTS SEE IT (BUD-285)
 *
 * 🚨 NOTHING BELOW MAY EVER BRANCH ON A FORMAT'S NAME. The whole screen is decided by facts
 * the format DECLARES — who sets the pace, whether there is an interval, whether the piece
 * ends when the athlete fails, whether its own clock fixes its duration. A `switch` on
 * `'tabata'` is the bug BUD-256 spent an issue removing from the engine, and importing it
 * into the app would undo that at the last possible moment.
 *
 * The facts arrive on the wire (`shared/contracts/sessions.ts`, `formatFactsSchema`, added by
 * BUD-277 for exactly this) rather than being restated here, which is what makes the promise
 * real: a seventh format that declares `paced: 'structure'` and an interval gets the
 * phase-driven layout with no change to a single line in this feature.
 * -------------------------------------------------------------------------------------- */

/**
 * ONE MOVEMENT OF ONE ROUND, as the screen reads it out.
 *
 * `count` is null on purpose and is not missing data: a ladder prints one scheme for every
 * movement in it and a death-by prints no number at all, so the prescription carries the
 * figure instead of the line. A row that printed "null reps" would be a rendering of that
 * case rather than a bug in it.
 */
export interface TimerMovement {
  id: string;
  /** The name a row prints. Carried, never looked up — the engine publishes no catalog. */
  name: string;
  count: number | null;
  /** `reps`, `m`, `cal` — the engine's own word, and never translated: it is data. */
  unit: string;
  /**
   * WHAT IS IN THE HANDS, IN KILOS. Null for an unloaded movement, or a room with nothing.
   *
   * 🚨 IT WAS MISSING, AND THAT IS THE "I DON'T SEE THE WEIGHTS" BUG. The piece carried the
   * count and the unit and dropped the load on the floor, so a timer running an AMRAP of
   * 24 kg kettlebell swings printed "15 KB Swings" — the one number an athlete has to walk to
   * the rack knowing, on the one screen they are looking at while doing it. Every other
   * surface in the app has printed it since the deck shipped.
   */
  kilos: number | null;
  /**
   * A LADDER'S RUNGS — 21-15-9. Null for every other shape.
   *
   * 🚨 IT WAS MISSING AND THE CLOCK PRINTED BARE NAMES. A ladder line carries NO count of its
   * own — the contract puts the scheme on the BLOCK, because one scheme governs every movement
   * in it — so a 21-15-9 of pull-ups and goblet squats reached the timer as `count: null` and
   * rendered as "Pull-Ups" and "Goblet Squats — 24 kg", on the one screen an athlete reads
   * mid-piece. The session screen printed it correctly all along, from the same payload; this
   * is the same fact carried the same way (`DeckMovementModel.scheme`).
   */
  scheme: readonly number[] | null;
  /**
   * A CAPPED-CHIPPER STATION'S OWN CLOCK, IN SECONDS — null for every other movement (BUD-367).
   * It is what `timer.converter.ts` reads to decide whether this piece needs a station clock
   * that moves the athlete on for itself, rather than the format's own `paced`: a for-time
   * piece is athlete-paced everywhere else in the library, and this is the one place a DECLARED
   * per-line fact overrides that for a single block, never a name checked anywhere.
   */
  stationCapSec: number | null;
}

/**
 * A PIECE, PLUS THE DECLARED FACTS THE SCREEN BRANCHES ON.
 *
 * The five fields in the middle are the format's own declaration, copied across unchanged by
 * `timer.converter.ts`. They are typed from the CONTRACT rather than restated as string
 * unions here, so a format fact that gains a value fails to compile in the converter — which
 * is the one place a person would then have to decide what the new value means.
 */
export interface TimerPiece {
  /** What the header calls this piece, or null when nothing named it. */
  title: string | null;
  /** WHO HOLDS THE THROTTLE — and, on its own, which of the two layouts renders. */
  paced: PacedBy;
  /**
   * What the piece is scored by. CARRIED AND NOT BRANCHED ON: the score is written down
   * after the piece, on the debrief, and neither layout renders anything different for it
   * today. It travels with the piece so the screen that does need it is not the one that has
   * to go and fetch a fact the timer already had.
   */
  scoredBy: ScoredBy;
  /** The window one round runs in, where the piece has one. The rail's tick. */
  intervalSec: number | null;
  /** The piece ends when the ATHLETE fails, so the clock must never end it for them. */
  endsAtFailure: boolean;
  /** The format's own clock fixes the duration and no cap is written on top of it. */
  durationIsFixed: boolean;
  /** The CLOCK decides how many rounds happen, rather than the shape — so no total is known. */
  roundsFromClock: boolean;
  /**
   * How many rounds or intervals the SHAPE declares, or null when nothing does.
   *
   * Null is the load-bearing case twice over: an AMRAP's round count is an output of the
   * clock, and a death-by's is an output of the athlete. A rail drawn against a fabricated
   * total would tell someone mid-piece that they are "4 / 16" through something that has no
   * sixteen.
   */
  rounds: number | null;
  /**
   * DOES ONE MOVEMENT GO PER INTERVAL, or does every round do all of them?
   *
   * Declared by the block's prescription — an alternating clock is an EMOM rotating through
   * its movements a minute at a time, which is what the bottom rail counts and what "up next"
   * names. When it is false no single movement is "on", so the layout lists the round rather
   * than pointing at one line of it.
   */
  rotates: boolean;
  /**
   * How many phases one round is made of: 1 when a round is one phase, 2 when its rest is a
   * phase of its own. It is what turns a phase index into a ROUND index, and it is carried
   * rather than counted at render time because the answer has to survive a resumed clock that
   * came back several boundaries later.
   */
  phasesPerRound: number;
  /** The movements of ONE round, in the order they are done. */
  movements: TimerMovement[];
  /** The same piece as the clock sees it: phases and a ceiling. */
  spec: TimerSpec;
}

/**
 * AN HONEST STOPWATCH — one open-ended round, no ceiling, nothing declared.
 *
 * The piece every format degrades to, and what the screen runs when no session has handed it
 * one. Not a fabricated twenty-minute cap: a ceiling nobody set would stop an athlete
 * mid-piece and file the attempt as capped.
 */
export const OPEN_PIECE: TimerPiece = {
  title: null,
  paced: "athlete",
  scoredBy: "total-time",
  intervalSec: null,
  endsAtFailure: false,
  durationIsFixed: false,
  roundsFromClock: false,
  rounds: null,
  rotates: false,
  phasesPerRound: 1,
  movements: [],
  spec: { phases: [{ id: "work", kind: "work", durationSec: null }], capSec: null },
};

/**
 * THE TWO LAYOUTS, AND THE ONE FACT THAT CHOOSES BETWEEN THEM.
 *
 * `self-paced` shows rounds done, the last round's comparison and the whole round listed —
 * and deliberately NO "up next", because in a piece the athlete paces we cannot know where
 * in the round they are. `phase-driven` shows two rails and the movement that is on, because
 * the structure knows exactly where they should be.
 */
export type TimerLayout = "self-paced" | "phase-driven";

export function layoutFor(piece: Pick<TimerPiece, "paced">): TimerLayout {
  return piece.paced === "athlete" ? "self-paced" : "phase-driven";
}

/* --- what a tap on the body does, if anything ------------------------------------------ */

/**
 * WHAT A TAP ON THE BODY MEANS RIGHT NOW — and `null` where it means NOTHING.
 *
 * 🚨 THE WHOLE BODY BEING THE BUTTON IS ONLY DEFENSIBLE WHERE THE BUTTON DOES SOMETHING. On an
 * EMOM it did not. Every phase of an every-minute piece is TIMED, and a timed phase is the
 * clock's to end — `advance` refuses to move one, and it has to: `positionAt` has already
 * walked past a spent phase by the time a tap is handled, so honouring the tap as well would
 * skip a whole minute. The result was a screen that buzzed the phone, advertised "Tap anywhere
 * = next" in a dashed pill, and moved nothing. The same was true of Tabata, of a death-by and
 * of an authored on/off interval piece: four of the six formats that land in the phase-driven
 * layout, and the owner reported it on the first one they ran.
 *
 * 🔑 IT IS STILL NOT A CHECK ON A FORMAT'S NAME. The question asked is whether the phase the
 * athlete is in has a LENGTH of its own, which is a fact the phase list carries. A max-reps
 * piece rests "as needed" — an open-ended phase that only a tap can end — so its tap survives,
 * and so does every structure-paced piece whose window nobody declared. A seventh format that
 * writes an open phase gets a working tap here with nothing changed.
 *
 *   `round`     — bank a round. Every athlete-paced piece: the tap IS the round counter, and it
 *                 works whether or not the phase under it is timed (an AMRAP is one long timed
 *                 phase and its tap is the entire score).
 *   `next`      — end an open-ended work phase and start the next one.
 *   `skip-rest` — end an open-ended rest. The one thing a tap can do on a phase-driven piece.
 *   `null`      — the clock owns this phase, or there is no phase after it. The body is inert,
 *                 nothing is advertised, and nothing buzzes.
 */
export type TimerTap = "round" | "next" | "skip-rest";

export function tapMeaning(
  layout: TimerLayout,
  spec: TimerSpec,
  position: Pick<TimerPosition, "phaseIndex">,
): TimerTap | null {
  if (layout === "self-paced") return "round";

  const phase = spec.phases[position.phaseIndex];
  // A phase with a declared length ends on the clock. Nothing an athlete does moves it.
  if (phase == null || phase.durationSec != null) return null;
  /* THE LAST PHASE HAS NOWHERE TO GO — `advancePhase` answers unchanged there, so a hint
     promising "next" would be the same lie one phase later. The piece ends through Finish. */
  if (position.phaseIndex >= spec.phases.length - 1) return null;

  return phase.kind === "rest" ? "skip-rest" : "next";
}

/* --- what the giant number is counting ------------------------------------------------- */

/**
 * WHICH WAY THE CLOCK RUNS. `down` towards a deadline, `up` from a start.
 *
 * A rendered value, never a stored one: the piece's declared facts decide it every frame, and
 * nothing anywhere sets it.
 */
export type ClockDirection = "down" | "up";

/** The giant number, and which way it is running. */
export interface GiantReading {
  seconds: number;
  direction: ClockDirection;
}

/**
 * 🚨 TIME REMAINING IS ALWAYS THE GIANT NUMBER — the handoff's first fixed rule, in bold, and
 * the one the screen used to resolve by accident. It was a `??` chain inline in the component
 * ending at the elapsed second, so a piece with neither a ceiling nor a timed phase counted UP
 * through a fallback nobody had chosen. It is a function here so it is a decision with a test
 * on it, and so the direction travels WITH the number to whatever renders it.
 *
 * THE TWO LAYOUTS ASK DIFFERENT QUESTIONS FIRST, and both answers are a countdown.
 *
 *   · Self-paced — the CEILING first. An AMRAP's athlete is running against the deadline the
 *     cap writes down; the single open phase behind it is the same piece with no cap on it.
 *   · Phase-driven — the PHASE first. They are racing this minute, not the sixteen. Where they
 *     are in the sixteen is what the rails are for, and a whole-piece countdown in 112px would
 *     answer a question nobody in the middle of an interval is asking.
 *
 * 🔑 THE ONE CASE THAT COUNTS UP, DELIBERATELY. With no cap and no timed phase there is
 * nothing to count down TO: "remaining" is undefined, and both alternatives are worse than a
 * stopwatch. A fabricated ceiling would stop an athlete mid-piece against a clock nobody set;
 * a frozen `0:00` would be the same lie the wrong-direction defect was. So an open-ended piece
 * counts up — and `direction` is RETURNED rather than re-derived at the call site, so the
 * caption above the number is forced to say `Elapsed` instead of `Remaining`. Counting up is
 * honest; counting up while claiming to count down is not, and that pairing is what this
 * return type exists to make impossible.
 */
export function giantReading(layout: TimerLayout, position: TimerPosition): GiantReading {
  const first = layout === "self-paced" ? position.capRemainingSec : position.phaseRemainingSec;
  const second = layout === "self-paced" ? position.phaseRemainingSec : position.capRemainingSec;

  const remaining = first ?? second;
  if (remaining != null) return { seconds: remaining, direction: "down" };
  return { seconds: position.elapsedSec, direction: "up" };
}

/* --- heart-rate zones ------------------------------------------------------------------ */

/**
 * THE FIVE EFFORT ZONES MOVED OUT — `src/shared/heart-rate/heart-rate.model.ts`.
 *
 * They were declared here, which was correct while a heart rate was a thing that happened on
 * the clock screen. It is not: a strap is paired on the session screen now, stays connected
 * through the warm-up, the lift and the cooldown, and its trace is written into the log — so
 * the zones are read by the session screen, the lift log, the debrief and two charts as well
 * as by this file. A vocabulary four features share is a cross-cutting module.
 *
 * The type is re-exported because `TimerGround` below is built out of it and `timer-ground.ts`
 * names it; the FUNCTIONS are not, so nothing acquires a second route to them.
 */
import type { HeartRateZone } from "@/shared/heart-rate";

export type { HeartRateZone };

/**
 * WHICH GROUND THE SCREEN PAINTS — near-black, work green, rest rust, or an effort zone.
 *
 * 🔑 THIS IS THE ONE PLACE THE HEART-RATE ZONE OVERRIDES THE GROUND (BUD-286), which is
 * what BUD-285 left this function as the sole answer FOR: five new grounds cost one branch
 * here and one row per zone in `timer-ground.ts`, and not one of the six elements that read a
 * colour on this screen had to change.
 *
 * 🚨 A ZONE OUTRANKS THE PHASE, and that is the design's call rather than an accident of
 * ordering. Work-green versus rest-rust says what the CLOCK wants; the zone says what the
 * athlete's body is actually doing, and when both are available the second is the one worth
 * the whole screen. The phase is still legible either way — the eyebrow says "Rest" in words.
 *
 * ⚠️ NULL IS THE ORDINARY CASE. No strap paired, a strap that dropped, a browser without
 * Bluetooth, and a phone that was never asked all arrive here as `null`, and every one of them
 * has to leave the timer exactly as BUD-285 shipped it. That is what makes the whole feature
 * removable: the zone path no-ops by having nothing to say.
 */
export type TimerGround = "self-paced" | "work" | "rest" | "hr-z1" | "hr-z2" | "hr-z3" | "hr-z4" | "hr-z5";

/** Written out rather than built from a template, so every ground this app can paint greps. */
const ZONE_GROUND: Record<HeartRateZone, TimerGround> = {
  z1: "hr-z1",
  z2: "hr-z2",
  z3: "hr-z3",
  z4: "hr-z4",
  z5: "hr-z5",
};

export function groundFor(
  layout: TimerLayout,
  phaseKind: TimerPhase["kind"],
  zone: HeartRateZone | null = null,
): TimerGround {
  if (zone != null) return ZONE_GROUND[zone];
  if (layout === "self-paced") return "self-paced";
  return phaseKind === "rest" ? "rest" : "work";
}

/* --- the rails ------------------------------------------------------------------------- */

/**
 * One bar of a rail. Three states rather than a boolean pair, because the CURRENT one is
 * drawn differently from both — taller and white, where done is accent and pending is a
 * hairline. The athlete finds their place on the rail by looking for the tall white bar.
 */
export type RailCell = "done" | "current" | "pending";

/**
 * A RAIL OF `total` BARS with `current` lit.
 *
 * Clamped rather than trusted: a phase index past the end of a rail is what a resumed clock
 * produces when the app comes back having crossed more boundaries than the rail has bars,
 * and an array with a hole in it renders as a gap the athlete reads as a missing round.
 *
 * An empty rail for a total of zero or less is the honest answer to "draw nothing" — see
 * {@link TimerPiece.rounds}: a piece whose total is unknown must not be given one.
 */
export function railCells(total: number, current: number): RailCell[] {
  const bars = Math.max(0, Math.floor(total));
  if (bars === 0) return [];
  const raw = Math.floor(current);
  /**
   * A NEGATIVE INDEX MEANS NOTHING IS CURRENT — the athlete is resting between rounds, and
   * no movement is on. Pointing at one they are not doing is worse than pointing at none.
   *
   * The upper clamp is the resumed-clock case: an app that came back having crossed more
   * boundaries than the rail has bars would otherwise leave a hole in the row, which reads
   * as a missing round rather than as an overrun.
   */
  const at = raw < 0 ? -1 : Math.min(raw, bars - 1);
  return Array.from({ length: bars }, (_, index) =>
    index === at ? "current" : index < at ? "done" : "pending",
  );
}

/**
 * WHICH ROUND THE ATHLETE IS IN, from the phase they are in.
 *
 * A round costs one phase or two — see {@link TimerPiece.phasesPerRound} — so this is the
 * only correct way to read a round off a phase index, and reading it any other way is what
 * puts an athlete on "round 8" of an eight-round Tabata halfway through round 4.
 */
export function roundIndexAt(piece: Pick<TimerPiece, "phasesPerRound">, phaseIndex: number): number {
  return Math.floor(Math.max(0, phaseIndex) / Math.max(1, piece.phasesPerRound));
}

/**
 * WHICH MOVEMENT IS ON, or null when no single one is.
 *
 * Null is the answer for every piece that does the whole round every round — most of them.
 * The layout renders the round's movements as a list in that case, which is more useful than
 * pointing at the first one and calling it current.
 */
export function movementIndexAt(
  piece: Pick<TimerPiece, "rotates" | "movements">,
  roundIndex: number,
): number | null {
  if (!piece.rotates || piece.movements.length === 0) return null;
  return Math.max(0, roundIndex) % piece.movements.length;
}

/* --- the last round, against the one before it ------------------------------------------- */

/**
 * WHAT THE LAST ROUND COST, AND WHETHER IT WAS FASTER THAN THE ONE BEFORE IT.
 *
 * The splits are CUMULATIVE elapsed seconds — the instant each round was banked — so a round's
 * own length is the difference between two of them, and the first round's length is its split
 * outright. Storing durations instead would lose the ability to say when anything happened.
 *
 * `deltaSec` is null after a single round and that is not "0": there is nothing to compare
 * against, and printing "same pace" for the first round of a piece is a comparison invented
 * out of one number.
 */
export interface RoundComparison {
  /** Which round this reading is about — 1-based, as the athlete counts. */
  round: number;
  /** How long that round took. */
  durationSec: number;
  /** Negative is faster than the round before it; null when there is no round before it. */
  deltaSec: number | null;
}

export function lastRoundComparison(splits: number[]): RoundComparison | null {
  if (splits.length === 0) return null;

  const last = splits[splits.length - 1] ?? 0;
  const previousSplit = splits.length >= 2 ? (splits[splits.length - 2] ?? 0) : 0;
  const durationSec = Math.max(0, last - previousSplit);

  if (splits.length < 3) {
    // Round 1 has no predecessor. Round 2 does — its predecessor is round 1, whose length is
    // simply its own split, which is why the branch is at three rather than at two.
    const deltaSec =
      splits.length === 2 ? durationSec - Math.max(0, previousSplit) : null;
    return { round: splits.length, durationSec, deltaSec };
  }

  const beforeThat = splits[splits.length - 3] ?? 0;
  return {
    round: splits.length,
    durationSec,
    deltaSec: durationSec - Math.max(0, previousSplit - beforeThat),
  };
}

/**
 * A DIFFERENCE, READ THE WAY A GYM READS ONE: `6s` under a minute, `1:12` above it.
 *
 * Always the MAGNITUDE — the sign is carried by the word beside it ("faster" / "slower") and
 * by the arrow, because "-6s faster" is a double negative someone at heart rate 175 has to
 * stop and parse.
 */
export function formatMagnitude(deltaSec: number): string {
  const magnitude = Math.abs(Math.round(deltaSec));
  return magnitude < 60 ? `${magnitude}s` : formatClock(magnitude);
}

/* -----------------------------------------------------------------------------------------
 * THE COUNTDOWN
 * -------------------------------------------------------------------------------------- */

/**
 * THE SECONDS BETWEEN THE TAP AND THE CLOCK.
 *
 * Ten. It was three, which is enough to put a phone down but not enough to get to the bar —
 * the athlete taps Start standing over the phone, and a metcon begins where the barbell is.
 * Ten is the count every gym clock in the world runs, so it needs no explaining, and it is
 * the only number here an athlete already knows by ear.
 *
 * It is NOT workout time: `startedAt` is stamped when the count reaches zero, so a 20:00 cap
 * is still 20:00. Anybody who does not want it taps through — see `skipCountdown`, which
 * starts the clock at the tap rather than shortening the count.
 *
 * ⚠️ IT ALSO FIXES THE TONES. `countdownTones` is `leadInTones(COUNTDOWN_SEC, 'go')`, and
 * `leadInTones` drops any beep that would land at or before zero. At three the first of the
 * three beeps fell exactly on zero and was dropped, so the count played beep-beep-GO — three
 * tones of the four-tone pattern every boundary in this timer teaches. At ten all three fit
 * (7 · 8 · 9 · GO), so the start sounds like every other downbeat.
 */
export const COUNTDOWN_SEC = 10;

/**
 * How long GO holds the screen after the count reaches zero.
 *
 * Without it GO exists for zero frames — the clock starts in the same tick the count ends,
 * so the athlete sees `1` and then the workout clock, and never the word every gym timer in
 * the world ends on. Lead-in, not workout time, for the same reason as the count itself.
 */
export const COUNTDOWN_GO_MS = 600;

/** The whole lead-in, in seconds: the offset between the tap and the first second of work. */
export const COUNTDOWN_TOTAL_SEC = COUNTDOWN_SEC + COUNTDOWN_GO_MS / 1000;

export type Countdown =
  /** Still counting: `secondsLeft` is {@link COUNTDOWN_SEC} down to 1, never 0 — zero is GO. */
  | { phase: "counting"; secondsLeft: number }
  /** The word, held for {@link COUNTDOWN_GO_MS}. The clock has NOT started yet. */
  | { phase: "go" }
  /** Over. The caller stamps the clock's start HERE, not at the tap. */
  | { phase: "done" };

/**
 * Where the countdown is, given milliseconds since the tap.
 *
 * DERIVED FROM THE WALL CLOCK, like everything else here, so a phone that locks at 0:01
 * comes back to a countdown that is over rather than one still sitting on 1.
 *
 * `ceil`, so the first thing on screen is {@link COUNTDOWN_SEC} (at 0ms) and the last is 1
 * (through the final 999ms). A `floor` would open one short and spend a whole second on 0,
 * which reads as a stalled timer.
 */
export function countdownAt(sinceTapMs: number): Countdown {
  const since = Math.max(0, sinceTapMs);
  const totalMs = COUNTDOWN_SEC * 1000;

  if (since < totalMs) {
    // Clamped to 1: at 2999ms `ceil` gives 1, but floating point a hair under `totalMs`
    // must never produce a 0 that would render as a number where GO belongs.
    return { phase: "counting", secondsLeft: Math.max(1, Math.ceil((totalMs - since) / 1000)) };
  }
  if (since < totalMs + COUNTDOWN_GO_MS) return { phase: "go" };
  return { phase: "done" };
}

/* -----------------------------------------------------------------------------------------
 * THE TONES
 * -------------------------------------------------------------------------------------- */

/**
 * One scheduled tone: how far from NOW it fires, and which of the three sounds it is.
 *
 * Three kinds rather than a pitch per event: what an athlete can actually tell apart at
 * heart rate 175 is two pitches and a rhythm. `go` means a work phase starts now, `beep` is
 * the lesser boundary and the counting-in, and `finish` is a RHYTHM rather than a fourth
 * pitch — which survives a phone under a towel in a loud gym better than any frequency does.
 */
export interface TimerTone {
  atSec: number;
  kind: "beep" | "go" | "finish";
}

/** Three beeps precede every downbeat, so a boundary is four tones and one learned pattern. */
const LEAD_IN_BEEPS = 3;

/**
 * How far ahead of a boundary the warning starts, in seconds — the issue's "beep 3s before
 * any phase change", as a number this module reads rather than a sentence.
 */
export const WARNING_LEAD_SEC = LEAD_IN_BEEPS;

/**
 * THE FOUR-TONE BOUNDARY: 3-2-1, then the thing itself.
 *
 * The downbeat lands exactly ON `atSec` and the beeps are the three seconds before it. The
 * lead-in is not decoration: a tone that arrives at the instant a round starts tells the
 * athlete about a round they are already late for, and someone mid-burpee cannot act on it.
 *
 * A beep that would fall at or before zero is DROPPED rather than emitted as a negative
 * offset. The only boundary that hits that is the first one of a piece, and it is already
 * led into — by the countdown, which is this same pattern spanning the seconds before the
 * clock starts.
 */
function leadInTones(atSec: number, kind: TimerTone["kind"]): TimerTone[] {
  const tones: TimerTone[] = [];
  for (let n = LEAD_IN_BEEPS; n >= 1; n--) {
    if (atSec - n > 0) tones.push({ atSec: atSec - n, kind: "beep" });
  }
  tones.push({ atSec, kind });
  return tones;
}

/** The countdown's own tones — the same four-tone pattern, at the lead-in's length. */
export function countdownTones(): TimerTone[] {
  return leadInTones(COUNTDOWN_SEC, "go");
}

/**
 * 🚨 WHEN THE PIECE MAKES A SOUND — and the one place the issue's text could not be honoured
 * as written (BUD-284, Decision 1).
 *
 * THE CONSTRAINT. iOS will not let a page make a sound that a user gesture did not initiate,
 * and it refuses SILENTLY — a tone scheduled outside the gesture simply never plays, with no
 * error anywhere. So every tone the whole piece will play is laid down in one pass inside the
 * Start tap's own handler (see `timer-audio.ts`), on the audio clock, at the moment of the
 * tap. Nothing has to be awake at 0:07 for the first beep to happen, which is exactly why it
 * still works with the phone on the floor and its timers throttled to once a minute.
 *
 * WHAT THAT COSTS. The issue asks for a beep three seconds before ANY phase change. For a
 * SELF-PACED piece the phase boundaries do not exist at tap time — they are created by the
 * athlete finishing a round, minutes later — so there is no instant to schedule a warning
 * for. This is a real deviation from the issue text, not an omission:
 *
 *   - **Structure-paced** (every phase knows its length): the full set of pre-warnings is
 *     scheduled up front. The issue's line is honoured exactly.
 *   - **Self-paced**: only what genuinely IS known at the tap — the countdown, and the cap
 *     warning where a cap exists. A pre-warning for a boundary nobody can predict is not
 *     promised, because it cannot be delivered.
 *
 * The alternative — scheduling a tone from a `setTimeout` at the moment the boundary is
 * created — is what the constraint above rules out: it is not a gesture, so on the primary
 * target device it is silence that looks like working code.
 *
 * `fromElapsedSec` re-bases the offsets on NOW, which is what a RESUME needs: a schedule laid
 * at Start describes a clock a pause has since stopped, so resuming asks for the remainder.
 */
export function pieceTones(spec: TimerSpec, fromElapsedSec = 0): TimerTone[] {
  const from = Math.max(0, fromElapsedSec);
  const tones: TimerTone[] = [];

  if (isStructurePaced(spec)) {
    let at = 0;
    for (const phase of spec.phases) {
      // The boundary is the START of each phase. The first one is at 0 and its lead-in is
      // the countdown, which is why `leadInTones` drops beeps that fall before zero.
      //
      // WORK GETS THE DOWNBEAT, REST GETS THE LESSER TONE. Two pitches is what an athlete
      // can still tell apart at heart rate 175, and which of the two it is answers the only
      // question they have at a boundary: go, or stop.
      tones.push(...leadInTones(at, phase.kind === "work" ? "go" : "beep"));
      at += phase.durationSec ?? 0;
    }
    // The end of the structure, or the cap if one arrives sooner. Both are the same event to
    // an athlete: the piece stops.
    const endsSec = spec.capSec == null ? at : Math.min(at, spec.capSec);
    tones.push(...leadInTones(endsSec, "finish"));
  } else if (spec.capSec != null && spec.capSec > 0) {
    // Self-paced. The cap is the one boundary that exists at tap time — see the header.
    tones.push(...leadInTones(spec.capSec, "finish"));
  }

  // `>=` rather than `>`: resuming exactly ON a boundary fires that boundary now, which is
  // the truth — the athlete restarted the clock at the minute mark.
  return tones
    .filter((tone) => tone.atSec >= from)
    .map((tone) => ({ atSec: tone.atSec - from, kind: tone.kind }));
}

/**
 * EVERYTHING THE START TAP SCHEDULES — the countdown, and the whole piece behind it.
 *
 * One schedule because there is only one gesture. The handoff from the countdown to the
 * running clock happens inside a ticker rather than under a finger, so an audio context
 * opened there would be suppressed; the piece's tones are pushed back by
 * {@link COUNTDOWN_TOTAL_SEC} and laid down at the tap alongside the countdown's.
 *
 * THE PIECE'S OWN TONE AT OFFSET 0 IS DROPPED, because the countdown's GO already IS the
 * first downbeat. Without this the athlete hears `beep beep beep GO GO`, the second GO
 * 0.6s behind the first — four tones is the pattern, five is a stutter that reads as a bug.
 *
 * SOUND OFF RETURNS AN EMPTY LIST, and that is how the switch is honoured by construction:
 * `scheduleTimerTones` opens no audio context at all for an empty schedule, so a muted
 * athlete's phone never touches the audio hardware. The switch is not a volume of zero.
 */
export function startTapTones(spec: TimerSpec, soundEnabled: boolean): TimerTone[] {
  if (!soundEnabled) return [];
  return [
    ...countdownTones(),
    ...pieceTones(spec)
      .filter((tone) => tone.atSec > 0)
      .map((tone) => ({ atSec: tone.atSec + COUNTDOWN_TOTAL_SEC, kind: tone.kind })),
  ];
}

/**
 * WHAT A RESUME SCHEDULES — the remainder, and no second countdown.
 *
 * A resume IS a tap, so the gesture iOS requires is present. No countdown, matching what the
 * clock does: the athlete paused to tie a shoe, not to walk to the bar.
 */
export function resumeTones(
  spec: TimerSpec,
  atElapsedSec: number,
  soundEnabled: boolean,
): TimerTone[] {
  return soundEnabled ? pieceTones(spec, atElapsedSec) : [];
}

/* -----------------------------------------------------------------------------------------
 * WHAT SURVIVES A RELAUNCH
 * -------------------------------------------------------------------------------------- */

/**
 * 🔑 THE KEY THE ANCHOR IS STORED UNDER, in the PREFERENCES store and never in the web
 * view's own storage.
 *
 * `src/app/platform/preferences.ts` is `UserDefaults` on device: it lives in the app's
 * container and iOS does not reclaim it. `localStorage` inside the app is WebKit's store,
 * and WebKit is ALLOWED TO EVICT IT when the device is short of space — nothing breaks
 * visibly when it does, the value is simply gone. For a cached list that means the app goes
 * quietly back to being slow; for this it means an athlete twelve minutes into a metcon
 * silently restarts at zero. Two other things in this codebase were moved off `localStorage`
 * for exactly this reason; the reasoning is written up in `preferences.ts` itself.
 *
 * The plugin prefixes its own `CapacitorStorage.` on device, so what is actually on the
 * phone is `CapacitorStorage.app.timer` in `Library/Preferences/<bundle id>.plist`.
 */
export const TIMER_STORAGE_KEY = "app.timer";

/**
 * A stored clock. THE ANCHOR, NEVER A COUNTDOWN VALUE — see the module header.
 *
 * `sessionId` is carried so two pieces cannot read each other's clock: a stored entry from
 * this morning's session must not resume under this evening's, and the id is the only thing
 * that can say which is which.
 */
export interface StoredTimer {
  sessionId: string;
  startedAt: number;
  pausedMs: number;
  pausedAt: number | null;
  phaseIndex: number;
  phaseStartedSec: number;
  rounds: number;
  /**
   * 🚨 HOW IT ENDED — null while it is still a clock, and the field that makes a STOPPED piece
   * storable at all.
   *
   * The stored entry used to describe only a clock that could be resumed, and finishing
   * DELETED it: a finished piece was "a clock waiting to be resumed by mistake". That was safe
   * while the debrief opened in the same breath as the clock stopping. It is not safe now that
   * the metcon hands the athlete back to their session — the accessory and the cooldown happen
   * in between, and an app iOS killed in that window took the whole score with it, because the
   * only copy was in memory.
   *
   * So the ending is stored WITH the reading, and it is what `parseStoredTimer` reads to bring
   * a piece back finished rather than running. The hazard the deletion existed for is answered
   * by the same field: a piece that says how it ended can never be resumed by mistake.
   */
  finishedBy: TimerFinishReason | null;
  /**
   * The elapsed second each round was banked at.
   *
   * ⚠️ THESE ARE STORED NOW AND THEY WERE NOT BEFORE. The objection on record was that
   * reconstructing splits from a round COUNT invents times nobody recorded — which is true, and
   * is an argument against DERIVING them, not against writing down the real ones. An athlete
   * whose phone was killed mid-AMRAP used to come back with seven rounds and no round times,
   * and the debrief then sent a score with nothing under it.
   */
  splits: number[];
}

export function serializeTimer(stored: StoredTimer): string {
  return JSON.stringify(stored);
}

/** One of the three endings, or not an ending at all. Read off a plist, so never trusted. */
function isFinishReason(value: unknown): value is TimerFinishReason {
  return TIMER_FINISH_REASONS.some((reason) => reason === value);
}

/** A finite number, or the fallback. `NaN` and `Infinity` both resume a clock at nonsense. */
function numberOr(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

/**
 * READ A STORED CLOCK BACK, or answer null.
 *
 * NULL FOR ANYTHING IT CANNOT VOUCH FOR: absent, unparseable, the wrong session, or missing
 * the one field the whole thing hangs on. A resume is a convenience and a WRONG resume is a
 * ruined workout, so every doubtful case degrades to "start fresh" rather than to a clock
 * anchored at `NaN` — which renders as `NaN:NaN` and cannot be recovered from without
 * reinstalling the app.
 *
 * `startedAt` must be a positive finite number: zero is the epoch, which as an anchor would
 * report an elapsed time of fifty-six years.
 */
export function parseStoredTimer(raw: string | null, sessionId: string): StoredTimer | null {
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;

  const value = parsed as Record<string, unknown>;
  if (value.sessionId !== sessionId) return null;

  const startedAt = numberOr(value.startedAt, 0);
  if (startedAt <= 0) return null;

  const pausedAt = numberOr(value.pausedAt, 0);

  return {
    sessionId,
    startedAt,
    pausedMs: Math.max(0, numberOr(value.pausedMs, 0)),
    pausedAt: pausedAt > 0 ? pausedAt : null,
    phaseIndex: Math.max(0, Math.floor(numberOr(value.phaseIndex, 0))),
    phaseStartedSec: Math.max(0, numberOr(value.phaseStartedSec, 0)),
    rounds: Math.max(0, Math.floor(numberOr(value.rounds, 0))),
    /* An ending this build does not know is no ending: the piece comes back as a clock, which
       is the answer this store gave for every entry ever written before the field existed. */
    finishedBy: isFinishReason(value.finishedBy) ? value.finishedBy : null,
    /* One bad number does not cost the others — a split is a stamp, not a running total, so a
       list with a hole in it is still the times that were actually banked. */
    splits: Array.isArray(value.splits)
      ? value.splits.filter((split): split is number => Number.isFinite(split) && split >= 0)
      : [],
  };
}

/* -----------------------------------------------------------------------------------------
 * THE TICK
 * -------------------------------------------------------------------------------------- */

/**
 * How often the screen re-reads the wall clock, in milliseconds.
 *
 * PURELY A RE-RENDER RATE. It is not the clock — nothing is counted at this interval and
 * nothing accumulates — so a tick that is late, early or skipped entirely costs a frame of
 * freshness and never a second of the piece. Four a second is enough that a whole-second
 * display never appears to stall, and cheap enough to run for twenty minutes.
 */
export const TICK_MS = 250;

/** The countdown's own rate. Faster, because GO holds for 600ms and 250ms can miss a fifth
 * of it. */
export const COUNTDOWN_TICK_MS = 100;

/**
 * SECONDS AS A CLOCK — `m:ss`, or `h:mm:ss` once there is an hour to show.
 *
 * The MINIMUM this screen needs and deliberately not a format system: the ten presentations
 * (a count-up, a count-down, a sub-clock, a cap in a header) are BUD-285's, and each of them
 * decides WHICH number to hand this, never how a number becomes a string. One reading rule
 * for the whole app is the part worth having in one place.
 *
 * No leading zero on the minutes, because a gym clock does not have one and `0:07` is read
 * faster than `00:07` at arm's length. Negative input is clamped: a clock cannot run backwards
 * and `-0:01` is a rendering of a bug rather than of a time.
 */
export function formatClock(totalSec: number): string {
  const total = Math.max(0, Math.floor(totalSec));
  const seconds = total % 60;
  const minutes = Math.floor(total / 60) % 60;
  const hours = Math.floor(total / 3600);
  const ss = String(seconds).padStart(2, "0");
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, "0")}:${ss}` : `${minutes}:${ss}`;
}
